import { memo, useMemo } from 'react';
import { cn, List } from '@divlab/divanui';

import styles from './Requisites.module.css';

import type { FC, HTMLAttributes, ReactElement } from 'react';
import type {
  AddressRequisite,
  NameRequisite,
  RequisitesData,
  UnknownRequisite,
} from '@Types/Requisites';

export interface RequisitesProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  requisites: RequisitesData;
}

function isAddressRequisite(
  requisite: UnknownRequisite | NameRequisite | AddressRequisite,
): requisite is AddressRequisite {
  return requisite.type === 'address';
}

const Requisites: FC<RequisitesProps> = (props) => {
  const { requisites, ...restProps } = props;
  const groups = useMemo(() => {
    return [requisites.primary, requisites.secondary]
      .filter((items) => items.length > 0)
      .map((group) =>
        group.map((requisite) => {
          if (isAddressRequisite(requisite)) {
            const addressValue = (
              <>
                <span itemProp='postalCode'>{requisite.value.postalCode}</span>
                {', '}
                <span itemProp='addressLocality'>{requisite.value.addressLocality}</span>
                {', '}
                <span itemProp='streetAddress'>{requisite.value.streetAddress}</span>
              </>
            );

            return (
              <div
                className={styles.paragraph}
                key={requisite.value.streetAddress}
                itemProp='address'
                itemScope
                itemType='http://schema.org/PostalAddress'
              >
                {requisite.name ? (
                  <>
                    {`${requisite.name}: `}
                    {addressValue}
                  </>
                ) : (
                  addressValue
                )}
              </div>
            );
          }

          if (requisite.type === 'name') {
            return (
              <div className={styles.paragraph} key={requisite.value}>
                {requisite.name ? (
                  <>
                    {`${requisite.name}: `}
                    <span itemProp='name'>{requisite.value}</span>
                  </>
                ) : (
                  <span itemProp='name'>{requisite.value}</span>
                )}
              </div>
            );
          }

          return (
            <div className={styles.paragraph} key={requisite.value}>
              {requisite.name ? `${requisite.name}: ${requisite.value}` : requisite.value}
            </div>
          );
        }),
      );
  }, [requisites.primary, requisites.secondary]);

  return (
    <div {...restProps}>
      <div className={styles.title}>{requisites.title}</div>
      <div className={cn(styles.content, styles.row)}>
        {groups.map((items, index) => (
          <List
            key={index}
            className={styles.item}
            items={items}
            renderChild={(listItem: ReactElement) => listItem}
          />
        ))}
      </div>
    </div>
  );
};

export default memo(Requisites);
